/**
 * 来源：foodbuy/market PageHeader；保留 eyebrow、标题、说明和操作位的同构签名。
 *
 * LeapWorldTwin-144「页面解剖」（11-ui-kit.md 新节）：每页恰好一个主动作（`primaryAction`），
 * 桌面固定页头右侧、移动端（≤768px）落为底部常驻操作条；次动作（`secondaryActions`，≤3 个）
 * 跟随渲染在主动作左侧，超过 3 个收进 `DropdownMenu`（不新起组件，复用包内已有实现）。
 * 旧 `action` 槽保留但标记 deprecated——仍然渲染（详情页/表单页历史调用点未必需要按钮语义的
 * primaryAction，如"返回列表"纯链接），新页面一律迁移到 primaryAction/secondaryActions。
 */
import type { ReactNode } from 'react'
import { Button, buttonClassNames } from './Button'
import { DropdownMenu } from './DropdownMenu'
import type { IconComponent } from './icons'

export interface PageHeaderPrimaryAction {
  label: ReactNode
  onClick?: () => void
  href?: string
  icon?: IconComponent
  disabled?: boolean
  /** 提交中态：转圈 + `aria-busy`，视觉与 Button loading 一致 */
  pending?: boolean
  /**
   * 表单页把提交按钮"上提"到页头时用（11-ui-kit §12 ④：表单页主动作即提交按钮）：
   * 原生 `form` 属性把页头按钮与页面下方的 `<form id=...>` 关联，无需额外 JS 转发提交事件。
   */
  type?: 'button' | 'submit'
  form?: string
}

export interface PageHeaderSecondaryAction {
  key: string
  label: ReactNode
  onClick?: () => void
  href?: string
  icon?: IconComponent
  disabled?: boolean
}

const SECONDARY_INLINE_MAX = 3

function ActionButton({
  action,
  variant,
  className = ''
}: {
  action: PageHeaderPrimaryAction | PageHeaderSecondaryAction
  variant: 'primary' | 'secondary'
  className?: string
}) {
  const Icon = action.icon
  const content = (
    <>
      {Icon ? <Icon size={16} aria-hidden="true" /> : null}
      {action.label}
    </>
  )
  if (action.href) {
    return (
      <a
        href={action.href}
        className={buttonClassNames({ variant, className: [action.disabled ? 'pointer-events-none opacity-50' : '', className].filter(Boolean).join(' ') })}
        aria-disabled={action.disabled || undefined}
      >
        {content}
      </a>
    )
  }
  // 只有 primaryAction 支持 pending/type/form（表单页把提交上提到页头，11-ui-kit §12 ④）；
  // secondaryAction 一律普通按钮，variant 参数已经区分两者，不用 'pending' in action 这类脆弱探针。
  const submitExtras = variant === 'primary' ? (action as PageHeaderPrimaryAction) : undefined
  return (
    <Button
      variant={variant}
      className={className}
      onClick={action.onClick}
      disabled={action.disabled}
      loading={submitExtras?.pending}
      type={submitExtras?.type ?? 'button'}
      form={submitExtras?.form}
    >
      {content}
    </Button>
  )
}

function SecondaryActions({ actions }: { actions: PageHeaderSecondaryAction[] }) {
  if (actions.length === 0) return null
  if (actions.length <= SECONDARY_INLINE_MAX) {
    return (
      <>
        {actions.map((action) => (
          <ActionButton key={action.key} action={action} variant="secondary" />
        ))}
      </>
    )
  }
  // 超过 3 个次动作收进 DropdownMenu（11-ui-kit「页面解剖」②）：避免页头被一排次按钮撑满。
  return (
    <DropdownMenu
      trigger="更多"
      triggerLabel="更多操作"
      align="end"
      items={actions.map((action) => ({
        key: action.key,
        label: action.label,
        disabled: action.disabled,
        onSelect: action.href ? () => { window.location.href = action.href! } : action.onClick
      }))}
    />
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  primaryAction,
  secondaryActions = []
}: {
  eyebrow: ReactNode
  title: ReactNode
  description: ReactNode
  /** @deprecated 迁移到 primaryAction / secondaryActions（11-ui-kit.md「页面解剖」）；仍会渲染，仅不参与移动端底部常驻条与守表 primaryAction 计数。 */
  action?: ReactNode
  /** 页面唯一主动作：同一个按钮节点，桌面态内联在页头右侧，移动端（≤768px）变形为底部常驻条。 */
  primaryAction?: PageHeaderPrimaryAction
  /** 次动作，≤3 个内联，超过收进 DropdownMenu。 */
  secondaryActions?: PageHeaderSecondaryAction[]
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-4">
      {/* min-w-0：长标题/不可断行内容在窄容器里允许收缩换行，不把操作位挤出 */}
      <div className="min-w-0">
        <p className="m-0 text-xs font-bold text-[var(--color-primary)]">{eyebrow}</p>
        <h1 className="m-0 mt-1 text-2xl font-bold tracking-tight text-[var(--color-ink)]">{title}</h1>
        <p className="m-0 mt-1.5 text-sm text-[var(--color-muted)]">{description}</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <SecondaryActions actions={secondaryActions} />
        {primaryAction ? (
          // 单一 DOM 节点、CSS 响应式类切换外观（不是渲染两份同名按钮）：
          // ≤768px 时这层容器变成 fixed 底部常驻条（11-ui-kit「页面解剖」①），
          // ≥768px 时退回页头内联的普通定位。position:fixed 不看 DOM 位置只看视口，
          // 放在 header 内部完全不影响移动端的视觉落点。
          // 早前版本渲染两个同名按钮（桌面隐藏一份 + 移动端隐藏另一份）在 jsdom 下
          // 两份都判定为"可见"，会让全仓一批 getByRole('button', { name }) 撞上
          // "Found multiple elements"（Twin-144 自查发现，apps/tickets 7 处测试真实撞过）。
          <span className="fixed inset-x-0 bottom-0 z-40 flex justify-stretch border-t border-[var(--color-line)] bg-[var(--color-panel)] px-4 py-3 md:static md:inset-auto md:z-auto md:inline-flex md:border-0 md:bg-transparent md:p-0">
            <ActionButton action={primaryAction} variant="primary" className="w-full justify-center md:w-auto" />
          </span>
        ) : null}
        {action}
      </div>
    </header>
  )
}
